import React, { useRef, useCallback, useEffect, useState } from 'react';
import TableElement from './TableElement';
import { usePlan } from './PlanContext';
import { formatDistance } from './seatUtils';

export default function PlanCanvas({ style }) {
  const {
    zoom, changeZoom, setSelectedId, setAssignMode,
    resolvedTables, assignMode,
    canvasW, canvasH, roomX0, roomY0, roomPxW, roomPxH,
    pxPerM, roomWm, roomHm,
    showDistances, snapToGrid,
    planData,
  } = usePlan();

  const pinch    = useRef({ dist: null });
  const gold     = planData?.brand_color || '#D4AF37';

  // ── Escape → sortir du mode assignation ──────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') { setAssignMode(null); setSelectedId(null); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [setAssignMode, setSelectedId]);

  const handleCanvasClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      setSelectedId(null);
      setAssignMode(null);
    }
  }, [setSelectedId, setAssignMode]);

  // ── Pinch to zoom (touch) ─────────────────────────────────────────────────
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      pinch.current.dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );
    }
  }, []);
  const handleTouchMove = useCallback((e) => {
    if (e.touches.length !== 2 || !pinch.current.dist) return;
    const nd = Math.hypot(
      e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY,
    );
    changeZoom((nd / pinch.current.dist - 1) * 0.4);
    pinch.current.dist = nd;
  }, [changeZoom]);
  const handleTouchEnd  = useCallback(() => { pinch.current.dist = null; }, []);

  // ── Wheel to zoom ─────────────────────────────────────────────────────────
  const handleWheel = useCallback((e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    changeZoom(e.deltaY > 0 ? -0.1 : 0.1);
  }, [changeZoom]);

  // ── Génération de la grille métrique ─────────────────────────────────────
  const gridLines = [];
  const totalWm = roomWm + 2;
  const totalHm = roomHm + 2;

  // Lignes verticales (tous les mètres dans la salle)
  for (let m = 0; m <= roomWm; m++) {
    const x = roomX0 + m * pxPerM;
    const isMajor = m % 5 === 0;
    gridLines.push(
      <line key={`v${m}`}
        x1={x} y1={roomY0} x2={x} y2={roomY0 + roomPxH}
        stroke={isMajor ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.04)'}
        strokeWidth={isMajor ? 1 : 0.5}
      />
    );
  }
  // Lignes horizontales
  for (let m = 0; m <= roomHm; m++) {
    const y = roomY0 + m * pxPerM;
    const isMajor = m % 5 === 0;
    gridLines.push(
      <line key={`h${m}`}
        x1={roomX0} y1={y} x2={roomX0 + roomPxW} y2={y}
        stroke={isMajor ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.04)'}
        strokeWidth={isMajor ? 1 : 0.5}
      />
    );
  }

  // ── Labels métriques (en haut + gauche) ───────────────────────────────────
  const metricLabels = [];
  // Haut : labels tous les mètres
  for (let m = 0; m <= roomWm; m += (pxPerM < 40 ? 2 : 1)) {
    const x = roomX0 + m * pxPerM;
    metricLabels.push(
      <text key={`lt${m}`}
        x={x} y={roomY0 - 6}
        fontSize={9} fill="rgba(0,0,0,0.35)" textAnchor="middle"
        fontFamily="'Montserrat',sans-serif">
        {m}m
      </text>
    );
  }
  // Gauche : labels
  for (let m = 0; m <= roomHm; m += (pxPerM < 40 ? 2 : 1)) {
    const y = roomY0 + m * pxPerM;
    metricLabels.push(
      <text key={`ll${m}`}
        x={roomX0 - 8} y={y + 3}
        fontSize={9} fill="rgba(0,0,0,0.35)" textAnchor="end"
        fontFamily="'Montserrat',sans-serif">
        {m}m
      </text>
    );
  }

  // ── Indicateur snap grille ─────────────────────────────────────────────────
  const snapDots = [];
  if (snapToGrid && zoom >= 0.6) {
    const step = 0.5 * pxPerM;
    const maxDots = Math.ceil(roomWm / 0.5 + 1) * Math.ceil(roomHm / 0.5 + 1);
    if (maxDots < 2000) {
      for (let mx = 0; mx <= roomWm; mx += 0.5) {
        for (let my = 0; my <= roomHm; my += 0.5) {
          const px = roomX0 + mx * pxPerM;
          const py = roomY0 + my * pxPerM;
          const isMajor = mx % 1 === 0 && my % 1 === 0;
          snapDots.push(
            <circle key={`d${mx}-${my}`}
              cx={px} cy={py} r={isMajor ? 1.5 : 0.8}
              fill={isMajor ? 'rgba(212,175,55,0.3)' : 'rgba(0,0,0,0.1)'}
            />
          );
        }
      }
    }
  }

  return (
    <div
      style={{
        flex: 1,
        overflow: 'auto',
        position: 'relative',
        background: '#F5F0E8',
        touchAction: 'pan-x pan-y',
        ...style,
      }}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Spacer scrollable */}
      <div style={{ width: canvasW * zoom, height: canvasH * zoom, position: 'relative', flexShrink: 0, minWidth: '100%', minHeight: '100%' }}>

        {/* Canvas scalé */}
        <div
          id="pdt-canvas-inner"
          onClick={handleCanvasClick}
          style={{
            position: 'absolute', top: 0, left: 0,
            width: canvasW, height: canvasH,
            transform: `scale(${zoom})`,
            transformOrigin: '0 0',
          }}
        >
          {/* ── SVG overlay : grille + salle + règles ─────────────────────── */}
          <svg
            width={canvasW} height={canvasH}
            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 1 }}
          >
            {/* Points de grille snap */}
            {snapDots}

            {/* Lignes de grille à l'intérieur de la salle */}
            {gridLines}

            {/* Ombre extérieure / fond de scène */}
            <rect
              x={roomX0 - 4} y={roomY0 - 4}
              width={roomPxW + 8} height={roomPxH + 8}
              rx={3} fill="rgba(0,0,0,0.06)"
            />

            {/* Remplissage de la salle */}
            <rect
              x={roomX0} y={roomY0}
              width={roomPxW} height={roomPxH}
              fill="#FEFAF5"
            />

            {/* Murs de la salle */}
            <rect
              x={roomX0} y={roomY0}
              width={roomPxW} height={roomPxH}
              fill="none"
              stroke="#1a1a2e"
              strokeWidth={4}
              rx={2}
            />

            {/* Hachures extérieur (murs) */}
            <defs>
              <pattern id="hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(0,0,0,0.12)" strokeWidth="2"/>
              </pattern>
            </defs>
            <rect x={0} y={0} width={canvasW} height={canvasH} fill="url(#hatch)" />
            {/* Masque pour laisser la salle propre */}
            <rect x={roomX0} y={roomY0} width={roomPxW} height={roomPxH} fill="#FEFAF5" />

            {/* Labels métriques */}
            {metricLabels}

            {/* Cote largeur salle */}
            <g>
              <line x1={roomX0} y1={roomY0 + roomPxH + 20} x2={roomX0 + roomPxW} y2={roomY0 + roomPxH + 20}
                stroke="rgba(0,0,0,0.4)" strokeWidth={1} markerEnd="url(#arr)" markerStart="url(#arr)" />
              <text x={roomX0 + roomPxW / 2} y={roomY0 + roomPxH + 34}
                fontSize={10} fill="rgba(0,0,0,0.55)" textAnchor="middle"
                fontFamily="'Montserrat',sans-serif" fontWeight={600}>
                {roomWm}m
              </text>
            </g>
            {/* Cote hauteur salle */}
            <g>
              <line x1={roomX0 + roomPxW + 20} y1={roomY0} x2={roomX0 + roomPxW + 20} y2={roomY0 + roomPxH}
                stroke="rgba(0,0,0,0.4)" strokeWidth={1} />
              <text x={roomX0 + roomPxW + 34} y={roomY0 + roomPxH / 2 + 4}
                fontSize={10} fill="rgba(0,0,0,0.55)" textAnchor="start"
                fontFamily="'Montserrat',sans-serif" fontWeight={600}>
                {roomHm}m
              </text>
            </g>

            {/* Lignes métriques grille */}
            {gridLines}
          </svg>

          {/* ── Tables ───────────────────────────────────────────────────── */}
          {resolvedTables.map(table => (
            <TableElement key={table.id} table={table} />
          ))}

          {/* Indicateur mode assignation */}
          {assignMode && (
            <div style={{
              position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
              background: 'rgba(212,175,55,0.95)', color: '#000',
              padding: '6px 14px', borderRadius: 20, fontSize: 11,
              fontWeight: 700, fontFamily: "'Montserrat',sans-serif",
              zIndex: 100, pointerEvents: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}>
              Cliquez sur un siège pour placer l'invité
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
